import { useContext, useEffect, useState } from "react";
import { Box } from "@mui/material";
import type { NavigateFunction } from "react-router-dom";
import { useNavigate, useParams } from "react-router-dom";
import { v4 } from "uuid";

import type { FormContextData, UUID } from "@thalamos/aspire-common";

import { api, apiHooks } from "../../api.js";
import { LoggedInUserContext } from "../../Contexts.js";
import { usePatientTimeline } from "../../hooks/apiCalls.js";
import { routeFns } from "../../routePaths.js";
import type { DefaultPageProps } from "../defaultProps.js";
import { DeclineRedoDialog } from "../FormProgressPage/admission/DeclineRedoDialog.js";
import type { DirectRedoRequest } from "../FormProgressPage/admission/redoRequest.js";
import {
  buildRedoRequestBannerBody,
  findDirectRedoRequestForUser,
  isAdmissionReadOnlyForRedo,
} from "../FormProgressPage/admission/redoRequest.js";
import { FormContextPdfViewer } from "../FormProgressPage/helpers/FormContextPdfViewer.js";
import { reviewableForms } from "../FormProgressPage/helpers/reviewableForms.js";
import {
  Banner,
  BannerList,
  LoadingSpinner,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import { FormFooterSection } from "~/components/form/index.js";
import { PatientBanner } from "~/components/layout/index.js";

/** API functions that can be injected for stories/tests. */
export interface RedoReviewApi {
  acceptRedo: (formId: UUID) => Promise<{ status: number; data?: unknown }>;
  declineRedo: (
    formId: UUID,
    body: { reason: string; reasonDescription?: string },
  ) => Promise<{ status: number; data?: unknown }>;
  createDraft: (draftId: string, formId: string) => Promise<{ status: number }>;
}

const defaultApi: RedoReviewApi = {
  acceptRedo: (formId) => api.forms.acceptRedo(formId),
  declineRedo: (formId, body) => api.forms.declineRedo(formId, body),
  createDraft: (draftId, formId) => api.drafts.create(draftId, { formId }),
};

export function RedoReviewPageInner({
  formContext,
  redoRequest,
  apiOverride,
}: {
  formContext: FormContextData;
  redoRequest: DirectRedoRequest;
  apiOverride?: RedoReviewApi;
}) {
  const apiClient = apiOverride ?? defaultApi;
  const navigate = useNavigate();
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { patientTimeline, reloadPatientTimeline } = usePatientTimeline({
    patientId: formContext.patientId,
  });
  const nhsNumber = patientTimeline?.patient?.nhsNumber ?? null;

  const redoFormId = redoRequest.form.id;
  const completedForms = reviewableForms(redoFormId, formContext);
  const hasCompletedForms = completedForms.length > 0;
  const [isPdfViewOpen, setIsPdfViewOpen] = useState<string | null>(
    completedForms.find((f) => f.id === redoFormId)?.id ??
      completedForms[0]?.id ??
      null,
  );

  const accept = async () => {
    setIsSubmitting(true);
    try {
      const result = await apiClient.acceptRedo(redoFormId);
      if (result.status === 200) {
        renderSuccessToast({ message: "Redo accepted" });
        const newFormId = (result.data as { newFormId?: string })?.newFormId;
        const admissionPage = routeFns.formContextPage(
          formContext.id,
          formContext.patientId,
        );
        if (newFormId) {
          // The replacement form has no draft of its own yet, so open one for the
          // author to complete. If that fails the redo is still accepted, so fall
          // back to the admission — where the redo can be continued — rather than
          // the preview, whose actions no longer apply once the redo is accepted.
          const newDraftId = v4();
          const draftResult = await apiClient.createDraft(
            newDraftId,
            newFormId,
          );
          if (draftResult.status === 201) {
            navigate(
              routeFns.formDraftsComplete(newDraftId, formContext.patientId),
            );
          } else {
            renderErrorToast({ message: "Failed to create draft" });
            navigate(admissionPage, { replace: true });
          }
        } else {
          navigate(admissionPage);
        }
      } else {
        const reason = (result.data as { reason?: string })?.reason;
        throw new Error(reason ?? "Unknown error");
      }
    } catch (e) {
      renderErrorToast({ message: (e as Error).message });
      setIsSubmitting(false);
    }
  };

  const decline = async (
    reason: string,
    reasonDescription: string | undefined,
  ) => {
    setIsSubmitting(true);
    try {
      const result = await apiClient.declineRedo(redoFormId, {
        reason,
        reasonDescription,
      });
      if (result.status === 204) {
        renderSuccessToast({ message: "Redo request declined" });
        navigate(
          routeFns.formContextPage(formContext.id, formContext.patientId),
        );
      } else {
        // 4xx resolves rather than rejects, so the server's reason is here to be
        // shown rather than replaced with generic copy.
        const reason = (result.data as { reason?: string })?.reason;
        throw new Error(reason ?? "Failed to decline redo request");
      }
    } catch (e) {
      renderErrorToast({ message: (e as Error).message });
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {rejectionDialogOpen && (
        <DeclineRedoDialog
          loading={isSubmitting}
          onClose={() => {
            setRejectionDialogOpen(false);
          }}
          onConfirm={(reason, reasonDescription) => {
            void decline(reason, reasonDescription);
          }}
        />
      )}
      <PatientBanner
        disableAutoMergeCheck
        isHidden
        patient={formContext.patient}
        patientTimeline={patientTimeline}
        nhsNumber={nhsNumber ?? undefined}
        reloadPatientTimeline={reloadPatientTimeline}
      />
      <Box sx={{ mb: 2 }}>
        <Banner
          body={buildRedoRequestBannerBody({
            requesterName: redoRequest.requesterName,
            reasons: redoRequest.reasons,
            additionalInfo: redoRequest.additionalInfo,
          })}
          bannerType={BannerList.WARNING}
        />
      </Box>
      {isPdfViewOpen && hasCompletedForms && (
        <Box sx={{ display: "flex", width: "100%", justifyContent: "center" }}>
          <Box sx={{ width: "750px" }}>
            <FormContextPdfViewer
              inModal={false}
              forms={completedForms}
              setFormId={setIsPdfViewOpen}
              formId={isPdfViewOpen}
            />
          </Box>
        </Box>
      )}
      <FormFooterSection
        isSticky={true}
        isForcedSticky={true}
        saveLabel={"Accept"}
        discardLabel={"Decline"}
        disableSubmit={isSubmitting}
        disableDiscard={isSubmitting}
        onSave={async () => {
          await accept();
        }}
        onCancel={() => {
          setRejectionDialogOpen(true);
        }}
      />
    </>
  );
}

function validateRedoIsReadyForReview(
  formContext: FormContextData | undefined | null,
  formLoading: boolean,
  userLoaded: boolean,
  redoActionable: boolean,
  navigate: NavigateFunction,
) {
  // Whether the redo is actionable depends on the logged-in user (authorship), so
  // decide nothing until both the form and the user have loaded — otherwise a
  // direct hit would redirect a genuine author away before their data arrives.
  if (formLoading || !userLoaded) return;
  if (!formContext) {
    navigate(routeFns.notFound(), { replace: true });
    return;
  }
  // Nothing to accept/decline here — already actioned, never theirs to action, or
  // read-only (archived/closed): fall back to the admission context rather than
  // showing the accept/decline preview.
  if (!redoActionable) {
    // Involuntary redirect, so replace rather than push — otherwise Back lands
    // on this page and bounces straight forward again, trapping the user.
    navigate(routeFns.formContextPage(formContext.id, formContext.patientId), {
      replace: true,
    });
  }
}

export function RedoReviewPage({}: DefaultPageProps) {
  const { formContextId } = useParams();
  const navigate = useNavigate();
  const userContext = useContext(LoggedInUserContext);
  const user = userContext?.user;

  const [{ data: formContext, loading: formLoading, response }] =
    apiHooks.forms.getFormContext(formContextId!);

  const redoRequest =
    formContext && user
      ? findDirectRedoRequestForUser(
          formContext,
          user.id,
          user.sessionContext?.teamId,
        )
      : undefined;

  // The redo is actionable here when there is a direct request for this user and
  // the admission is not read-only (archived/closed). Holding the admission's
  // claim is deliberately NOT required: the responder is the designated actor, so
  // a colleague who has taken the claim must not be able to strand them.
  const redoActionable =
    !!formContext &&
    !!redoRequest &&
    !!user &&
    !isAdmissionReadOnlyForRedo(formContext);

  useEffect(
    () =>
      validateRedoIsReadyForReview(
        formContext,
        formLoading,
        !!user,
        redoActionable,
        navigate,
      ),
    [formContext, formLoading, user, redoActionable, navigate],
  );

  if (
    formLoading ||
    response?.status !== 200 ||
    !formContext ||
    !redoRequest ||
    !redoActionable
  ) {
    return <LoadingSpinner />;
  }

  return (
    <RedoReviewPageInner formContext={formContext} redoRequest={redoRequest} />
  );
}
